import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import AuthCallback from "@/components/AuthCallback";
import Sidebar from "@/components/Sidebar";
import LoginPage from "@/pages/LoginPage";
import DashboardPage from "@/pages/DashboardPage";
import CoachesPage from "@/pages/CoachesPage";
import DozentenPage from "@/pages/DozentenPage";
import CalendarPage from "@/pages/CalendarPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import AcceptInvitePage from "@/pages/AcceptInvitePage";
import AvgsPage from "@/pages/AvgsPage";
import AvgsClientDetailPage from "@/pages/AvgsClientDetailPage";
import ClientHomePage from "@/pages/ClientHomePage";
import TerminePage from "@/pages/TerminePage";
import WochenUebersichtPage from "@/pages/WochenUebersichtPage";
import VertretungsplanPage from "@/pages/VertretungsplanPage";
import TerminplanungPage from "@/pages/TerminplanungPage";
import TermineBestaetigenPage from "@/pages/TermineBestaetigenPage";
import TerminBestaetigungPage from "@/pages/TerminBestaetigungPage";
import CoachSignPage from "@/pages/CoachSignPage";
import BrandingGuidePage from "@/pages/BrandingGuidePage";
import BrandGuidePublicPage from "@/pages/BrandGuidePublicPage";
import KundenStatusPage from "@/pages/KundenStatusPage";
import KundenArchivPage from "@/pages/KundenArchivPage";
import ZertifikatePage from "@/pages/ZertifikatePage";
import WeiterbildungPage from "@/pages/WeiterbildungPage";
import WeiterbildungClassDetailPage from "@/pages/WeiterbildungClassDetailPage";
import WeiterbildungParticipantHomePage from "@/pages/WeiterbildungParticipantHomePage";
import ParticipantSignPage from "@/pages/ParticipantSignPage";
import AvgsAppointmentSignPage from "@/pages/AvgsAppointmentSignPage";
import ChatPage from "@/pages/ChatPage";
import LicensesPage from "@/pages/LicensesPage";
import InboxPage from "@/pages/InboxPage";
import KpiPage from "@/pages/KpiPage";
import TeamProjectmanagementPage from "@/pages/TeamProjectmanagementPage";
import PlacementSurveyPage from "@/pages/PlacementSurveyPage";
import WeiterbildungKundeAktePage from "@/pages/WeiterbildungKundeAktePage";
import SetupWizardPage from "@/pages/SetupWizardPage";
import InterneAuditPage from "@/pages/InterneAuditPage";
import MyCertificatesPage from "@/pages/MyCertificatesPage";
import UnterlagenPruefungPage from "@/pages/UnterlagenPruefungPage";
import TraegerPage from "@/pages/TraegerPage";
import PlattformPage from "@/pages/PlattformPage";
import PaymentResultPage from "@/pages/PaymentResultPage";
import { ImpersonationBar, AuditGuard, LockGate } from "@/components/PlanBanners";
import AbrechnungPage from "@/pages/AbrechnungPage";
import ManagementPage from "@/pages/ManagementPage";
import CapacityPage from "@/pages/CapacityPage";
import EmailLogPage from "@/pages/EmailLogPage";
import TraegerbestaetigungPage from "@/pages/TraegerbestaetigungPage";
import ZertifikatPruefenPage from "@/pages/ZertifikatPruefenPage";
import VertraegePage from "@/pages/VertraegePage";
import VertragSignPage from "@/pages/VertragSignPage";
import SurveyResponsePage from "@/pages/SurveyResponsePage";
import AvgsFeedbackPage from "@/pages/AvgsFeedbackPage";
import ComplaintFormPage from "@/pages/ComplaintFormPage";
import QuestionnairePage from "@/pages/QuestionnairePage";
import SurveyResultsPage from "@/pages/SurveyResultsPage";
import WeeklyControlPage from "@/pages/WeeklyControlPage";
import DesignerAccessPage from "@/pages/DesignerAccessPage";
import BrandingStudioPage from "@/pages/BrandingStudioPage";
import BrandPreviewPage from "@/pages/BrandPreviewPage";

function AppShell({ children }) {
  return (
    <div className="min-h-screen bg-slate-50 flex">
      <Sidebar />
      <main className="flex-1 min-w-0 pt-14 md:pt-0"><ImpersonationBar /><LockGate>{children}</LockGate></main>
    </div>
  );
}

function AppRouter() {
  const location = useLocation();
  const { user } = useAuth();
  // Synchronously detect Emergent OAuth callback
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  const isClient = user && user.role === "client";
  const isParticipant = user && user.role === "participant";
  const isDesigner = user && user.role === "designer";
  const homePath = isClient ? "/mein-bereich" : isParticipant ? "/weiterbildung/mein-bereich" : isDesigner ? "/branding" : "/dashboard";
  // Designer-Zugang ist hart auf das Branding-Studio beschränkt — egal welche Route
  // angefragt wird, es gibt keine Sidebar, keine Kundendaten, keine anderen Funktionen.
  if (isDesigner && location.pathname !== "/branding") {
    return <Navigate to="/branding" replace />;
  }
  return (
    <Routes>
      <Route path="/" element={<Navigate to={homePath} replace />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/accept-invite" element={<AcceptInvitePage />} />
      <Route path="/anwesenheit/:token" element={<ParticipantSignPage />} />
      <Route path="/termin-unterschreiben/:token" element={<AvgsAppointmentSignPage />} />
      <Route path="/vertrag-unterschreiben/:token" element={<VertragSignPage />} />
      <Route path="/umfrage/:token" element={<SurveyResponsePage />} />
      <Route path="/feedback/:token" element={<AvgsFeedbackPage />} />
      <Route path="/zertifikat/:code" element={<ZertifikatPruefenPage />} />
      <Route path="/beschwerde/:token" element={<ComplaintFormPage />} />
      <Route path="/fragebogen/:token" element={<QuestionnairePage />} />
      <Route path="/termine-bestaetigen/:token" element={<TermineBestaetigenPage />} />
      <Route path="/meine-termine/:token" element={<TerminBestaetigungPage />} />
      <Route path="/coach-unterschrift/:token" element={<CoachSignPage />} />
      <Route path="/marken-guide/:token" element={<BrandGuidePublicPage />} />
      <Route path="/designer-access/:token" element={<DesignerAccessPage />} />
      <Route path="/brand-preview/:profileId" element={<BrandPreviewPage />} />
      <Route
        path="/branding"
        element={
          <ProtectedRoute allowedRoles={["designer"]}>
            <BrandingStudioPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/weiterbildung/mein-bereich"
        element={
          <ProtectedRoute>
            <AppShell>
              <WeiterbildungParticipantHomePage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/weiterbildung"
        element={
          <ProtectedRoute permKey="weiterbildung">
            {isParticipant ? <Navigate to="/weiterbildung/mein-bereich" replace /> : <AppShell><WeiterbildungPage /></AppShell>}
          </ProtectedRoute>
        }
      />
      <Route
        path="/weiterbildung/kunden/:participantId"
        element={
          <ProtectedRoute permKey="weiterbildung">
            {isParticipant ? <Navigate to="/weiterbildung/mein-bereich" replace /> : <AppShell><WeiterbildungKundeAktePage /></AppShell>}
          </ProtectedRoute>
        }
      />
      <Route
        path="/weiterbildung/klasse/:classId"
        element={
          <ProtectedRoute permKey="weiterbildung">
            {isParticipant ? <Navigate to="/weiterbildung/mein-bereich" replace /> : <AppShell><WeiterbildungClassDetailPage /></AppShell>}
          </ProtectedRoute>
        }
      />
      <Route
        path="/mein-branding"
        element={
          <ProtectedRoute>
            <BrandingGuidePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/mein-bereich"
        element={
          <ProtectedRoute>
            <AppShell>
              <ClientHomePage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            {isClient ? <Navigate to="/mein-bereich" replace /> : isParticipant ? <Navigate to="/weiterbildung/mein-bereich" replace /> : <AppShell><DashboardPage /></AppShell>}
          </ProtectedRoute>
        }
      />
      <Route
        path="/kalender"
        element={
          <ProtectedRoute allowedRoles={["admin", "manager", "coach", "lecturer"]} permKey="kalender">
            <AppShell>
              <CalendarPage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/avgs"
        element={
          <ProtectedRoute allowedRoles={["admin", "manager", "coach", "lecturer"]} permKey="avgs">
            <AppShell>
              <AvgsPage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/avgs/:clientId"
        element={
          <ProtectedRoute allowedRoles={["admin", "manager", "coach", "lecturer"]} permKey="avgs">
            <AppShell>
              <AvgsClientDetailPage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/termine"
        element={
          <ProtectedRoute allowedRoles={["admin", "manager", "coach"]} permKey="termine">
            {isClient ? <Navigate to="/mein-bereich" replace /> : <AppShell><TerminePage /></AppShell>}
          </ProtectedRoute>
        }
      />
      <Route path="/terminplanung" element={<ProtectedRoute allowedRoles={["admin", "manager", "coach"]} permKey="termine"><AppShell><TerminplanungPage /></AppShell></ProtectedRoute>} />
      <Route path="/vertretungsplan" element={<ProtectedRoute allowedRoles={["admin", "manager", "coach"]} permKey="termine"><AppShell><VertretungsplanPage /></AppShell></ProtectedRoute>} />
      <Route
        path="/wochenuebersicht"
        element={
          <ProtectedRoute allowedRoles={["admin", "manager"]} permKey="wochenuebersicht">
            <AppShell>
              <WochenUebersichtPage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/zertifikate"
        element={
          <ProtectedRoute allowedRoles={["admin", "manager"]} permKey="kunden_zertifikate">
            <AppShell>
              <ZertifikatePage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/kunden-archiv"
        element={
          <ProtectedRoute allowedRoles={["admin", "manager", "coach", "lecturer"]} permKey="kunden_archiv">
            <AppShell>
              <KundenArchivPage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/kunden-status"
        element={
          <ProtectedRoute allowedRoles={["admin", "manager"]} permKey="kunden_status">
            <AppShell>
              <KundenStatusPage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route path="/entries" element={<ProtectedRoute allowedRoles={["admin", "manager", "coach", "lecturer"]} permKey="entries"><AppShell><KpiPage timeOnly /></AppShell></ProtectedRoute>} />
      <Route
        path="/coaches"
        element={
          <ProtectedRoute allowedRoles={["admin", "manager"]} permKey="coaches">
            <AppShell>
              <CoachesPage key="coaches" />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/mitarbeiter"
        element={
          <ProtectedRoute allowedRoles={["admin", "manager"]} permKey="coaches">
            <AppShell>
              <CoachesPage mode="staff" key="staff" />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/dozenten"
        element={
          <ProtectedRoute allowedRoles={["admin", "manager"]} permKey="dozenten">
            <AppShell>
              <DozentenPage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/weiterbildung/chat"
        element={
          <ProtectedRoute allowedRoles={["admin", "manager", "coach", "lecturer", "participant"]} permKey="wb_chat">
            <AppShell>
              <ChatPage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/lizenzen"
        element={
          <ProtectedRoute allowedRoles={["admin", "manager"]} permKey="lizenzen">
            <AppShell>
              <LicensesPage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/postfach"
        element={
          <ProtectedRoute allowedRoles={["admin", "manager"]} permKey="postfach">
            <AppShell>
              <InboxPage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/mein-arbeitstag"
        element={<Navigate to="/kpi" replace />}
      />
      <Route
        path="/kpi"
        element={
          <ProtectedRoute allowedRoles={["admin", "manager", "coach", "lecturer"]} permKey="kpi">
            <AppShell>
              <KpiPage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/team-pm"
        element={
          <ProtectedRoute allowedRoles={["admin", "manager", "coach", "lecturer"]} permKey="team_pm">
            <AppShell>
              <TeamProjectmanagementPage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/vertraege"
        element={
          <ProtectedRoute allowedRoles={["admin", "manager"]} permKey="vertraege">
            <AppShell>
              <VertraegePage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route path="/interne-audit" element={<ProtectedRoute allowedRoles={["admin"]} permKey="interne_audit"><AppShell><AuditGuard><InterneAuditPage /></AuditGuard></AppShell></ProtectedRoute>} />
      <Route path="/abrechnung" element={<ProtectedRoute allowedRoles={["admin", "manager", "coach", "lecturer"]}><AppShell><AbrechnungPage /></AppShell></ProtectedRoute>} />
      <Route path="/payment/success" element={<ProtectedRoute><AppShell><PaymentResultPage /></AppShell></ProtectedRoute>} />
      <Route path="/payment/cancel" element={<ProtectedRoute><AppShell><PaymentResultPage cancelled /></AppShell></ProtectedRoute>} />
      <Route path="/plattform" element={<ProtectedRoute allowedRoles={["admin"]}><AppShell><PlattformPage /></AppShell></ProtectedRoute>} />
      <Route path="/traeger" element={<ProtectedRoute allowedRoles={["admin"]}><AppShell><TraegerPage /></AppShell></ProtectedRoute>} />
      <Route path="/unterlagen-pruefung" element={<ProtectedRoute allowedRoles={["admin", "manager", "coach", "lecturer"]} permKey="unterlagen"><AppShell><AuditGuard><UnterlagenPruefungPage /></AuditGuard></AppShell></ProtectedRoute>} />
      <Route path="/meine-zertifikate" element={<ProtectedRoute allowedRoles={["admin", "manager", "coach", "lecturer"]} permKey="zertifikate"><AppShell><MyCertificatesPage /></AppShell></ProtectedRoute>} />
      <Route
        path="/einrichtung"
        element={
          <ProtectedRoute allowedRoles={["admin", "manager"]}>
            <AppShell>
              <SetupWizardPage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/verbleibsquote"
        element={
          <ProtectedRoute allowedRoles={["admin"]} permKey="verbleibsquote">
            <AppShell>
              <AuditGuard><PlacementSurveyPage /></AuditGuard>
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/umfrage-ergebnisse"
        element={
          <ProtectedRoute allowedRoles={["admin"]} permKey="umfrage_ergebnisse">
            <AppShell>
              <AuditGuard><SurveyResultsPage /></AuditGuard>
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/management"
        element={
          <ProtectedRoute>
            {user?.is_master_admin ? (
              <AppShell>
                <ManagementPage />
              </AppShell>
            ) : (
              <Navigate to={homePath} replace />
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/meine-kapazitaet"
        element={
          <ProtectedRoute allowedRoles={["admin", "manager", "coach", "lecturer"]} permKey="meine_kapazitaet">
            <AppShell>
              <CapacityPage mine />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/kapazitaet"
        element={
          <ProtectedRoute allowedRoles={["admin", "manager"]} permKey="coach_capacity">
            <AppShell>
              <CapacityPage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route path="/traegerbestaetigung" element={<ProtectedRoute allowedRoles={["admin", "manager"]} permKey="traegerbestaetigung"><AppShell><TraegerbestaetigungPage /></AppShell></ProtectedRoute>} />
      <Route
        path="/email-protokoll"
        element={
          <ProtectedRoute allowedRoles={["admin"]} permKey="email_log">
            <AppShell>
              <EmailLogPage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/wochenkontrolle"
        element={
          <ProtectedRoute allowedRoles={["admin", "manager"]} permKey="wochenkontrolle">
            <AppShell>
              <WeeklyControlPage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to={homePath} replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRouter />
        <Toaster position="top-right" richColors closeButton />
      </BrowserRouter>
    </AuthProvider>
  );
}
